import { CartButton } from '@components/cartbutton/spa';
import { PLACEHOLDERS, SafeImage } from '@components/image';
import { TProduct, TStore, TStoreProductCategory } from '@components/types';
import { inventoryByVariantId } from '@components/variant/variantSelector';
import { getFirst, getImageObject, humanizeCurrency, sanityIoImageLoader } from '@core/utils';
import React from 'react';

type Props = {
    category?: TStoreProductCategory
    element?: TProduct
    setSelectedProduct?: Function
    setIsOpen?: Function
    isOpen?: boolean
    store?: TStore
    tableInfo?: any
    isStoreOpen?: boolean
}

// Catalog names often chain several titles with "|" ("Gift Pack|Premium Chocolate | Diwali"). Render those as
// separate phrases so the line can break at sensible places instead of in the middle of one.
const displayName = (name?: string) => (name || '').replace(/\s*\|\s*/g, ' · ').replace(/\s{2,}/g, ' ').trim();

const SpecialProductCardV2: React.FC<Props> = ({ element, setSelectedProduct, setIsOpen, isOpen, store, tableInfo, isStoreOpen = true }) => {
    const orderingEnabled = !!(store?.storemeta?.functions?.online_ordering || tableInfo?.table_id > 0);
    const inStock = !!(element?.inventories?.some((inventory) => inventory?.quantity > 0) || element?.inventories?.some((inventory) => inventory?.always_available));
    const isLive = element?.inventories?.some((inventory) => inventory?.is_live ); 
    const productAvailable = orderingEnabled && inStock && isStoreOpen && isLive;
    // Say why a product can't be added instead of a bare "unavailable".
    const unavailableReason = !orderingEnabled ? 'Ordering not available' : !isStoreOpen ? 'Store closed' : 'Out of stock';

    const inventory = element?.inventories?.[0];
    const price = inventory?.price;
    const mrp = inventory?.mrp;
    const onSale = mrp > price;
    const hasImage = element?.images?.length > 0;
    const themeText = store?.storemeta?.settings?.shop_page_theme?.lighttext || '';
    const muted = !productAvailable ? 'text-neutral-500' : '';

    return (
        <div className="flex items-start flex-row w-full min-w-0 gap-3 pb-2">
            <div className="flex flex-col flex-1 min-w-0 gap-1 font-manrope">
                <p
                    style={{ color: themeText }}
                    className={`m-0 text-[15px] leading-snug font-semibold line-clamp-3 break-words ${muted}`}>
                    {displayName(element?.name)}
                </p>
                <p
                    style={{ color: themeText }}
                    className={`m-0 flex items-baseline gap-1.5 text-base font-semibold ${!productAvailable ? 'text-neutral-500' : 'text-neutral-800'}`}>
                    {humanizeCurrency(price)}
                    {onSale && <span className="text-xs font-medium text-neutral-400 line-through">{humanizeCurrency(mrp)}</span>}
                </p>
                {element?.description?.length > 0 && (
                    <p
                        style={{ color: themeText }}
                        className={`m-0 text-sm leading-snug font-normal line-clamp-2 break-words ${!productAvailable ? 'text-neutral-500' : 'text-neutral-600'}`}>
                        {element?.description}
                    </p>
                )}
                {!productAvailable && (
                    <span className="self-start rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-semibold text-neutral-500">
                        {unavailableReason}
                    </span>
                )}
            </div>
            <div className="flex-shrink-0 flex flex-col items-center">
                {hasImage && (
                    <div
                        className="relative"
                        onClick={productAvailable ? () => { setSelectedProduct(element); setIsOpen(!isOpen); } : undefined}
                    >
                        <SafeImage
                            loader={sanityIoImageLoader}
                            src={getImageObject(element?.images)?.url}
                            fallback={PLACEHOLDERS.product}
                            alt={getImageObject(element?.images)?.description || element?.name || 'Product'}
                            width="100"
                            height="100"
                            className={`w-24 h-24 object-cover aspect-square rounded-xl bg-neutral-100 ${!productAvailable ? 'filter grayscale' : ''}`}
                        />
                    </div>
                )}
                {productAvailable && (
                    <div className={`${hasImage ? '-mt-4' : ''} relative flex justify-center items-center w-22`}>
                        <CartButton
                            mode={''}
                            store={store}
                            Isvariant={false}
                            product={{ ...element }}
                            variantId={element?.inventories[0]?.is_available && element?.inventories[0]?.variant
                                ? element?.inventories[0]?.variant?.id
                                : element?.variants?.filter(
                                    (variant) =>
                                        getFirst(inventoryByVariantId(variant, element))?.is_available ==
                                        true
                                )[0]?.id}
                            inventory={element?.inventories[0]}
                        ></CartButton>
                    </div>
                )}
                {productAvailable && element?.variants?.length > 1 && (
                    <div
                        style={{ color: themeText }}
                        className="pt-0.5 text-xs font-medium font-manrope text-neutral-500 text-center">
                        Customisable
                    </div>
                )}
            </div>
        </div>
    );
};

export default SpecialProductCardV2;
